import { IconHourglass, IconRefreshCcw } from '@iwoca/icons';
import { clsx } from 'clsx';

import { CopyEmailButton } from './CopyEmailButton';
import { EmptyValueDash } from './EmptyValueDash';
import { displayCurrency } from '../../../../../utils/DisplayCurrency';
import { CustomerRow } from '../../../CustomersV2.types';
import { daysSinceExpiry, daysUntilExpiry } from '../../../expiryDays';
import { isSpendableLimit } from '../../../spendableLimit';

type ExpiryIcon = 'hourglass' | 'refresh' | null;

interface ExpirySubtext {
  icon: ExpiryIcon;
  text: string;
}

const expiredText = (daysAgo: number): string => {
  // `daysSinceExpiry` reads 0 for a timeout earlier today and -1 at the moment it
  // lapses, so the whole expiry day lands here rather than on a "0d ago".
  if (daysAgo <= 0) return 'Exp today';
  return daysAgo < 365 ? `Exp ${daysAgo}d ago` : 'Exp a year+ ago';
};

/**
 * Keyed on the timeout rather than the status: the note is what explains an
 * amount the cell has stopped painting green, so every expired limit needs one.
 */
const expirySubtext = (row: CustomerRow): ExpirySubtext | null => {
  if (row.limit_timeout === null) return null;

  const daysLeft = daysUntilExpiry(row.limit_timeout);
  if (daysLeft > 0) return { icon: 'hourglass', text: `${daysLeft}d left` };

  // A refresh icon only where there is something to refresh: a limit we can't
  // estimate has no amount, and only `last_approved_offer` invites the seller to
  // ask for a new decision.
  const canRefresh =
    row.spending_limit_status.value === 'last_approved_offer' &&
    row.limit_amount !== null;

  return {
    icon: canRefresh ? 'refresh' : null,
    text: expiredText(daysSinceExpiry(row.limit_timeout)),
  };
};

const ExpiryIconGlyph = ({ icon }: { icon: ExpiryIcon }) => {
  if (icon === 'hourglass')
    return (
      <IconHourglass
        data-testid="expiry-icon-hourglass"
        height="10"
        width="10"
      />
    );
  if (icon === 'refresh')
    return (
      <IconRefreshCcw
        data-testid="expiry-icon-refresh"
        height="10"
        width="10"
      />
    );
  return null;
};

const ExpirySubtextLabel = ({ subtext }: { subtext: ExpirySubtext }) => (
  <div className="flex items-center gap-xs text-xs font-reg text-structure-60">
    <ExpiryIconGlyph icon={subtext.icon} />
    {subtext.text}
  </div>
);

export const ToSpendCell = ({
  row,
  /**
   * Mobile cards print the amount alone: the card spells the status out in words
   * on the line above, so the expiry note would repeat what the seller has just
   * read.
   */
  amountOnly = false,
}: {
  row: CustomerRow;
  amountOnly?: boolean;
}) => {
  const isDeclined = row.application_stage.value === 'declined';
  const subtext = amountOnly || isDeclined ? null : expirySubtext(row);
  // Green marks a limit the customer can still spend, in both layouts.
  const amountClassName = clsx(isSpendableLimit(row) && 'text-success-mid');

  if (isDeclined || row.limit_amount === null) {
    return (
      <div className="flex w-full items-center gap-l">
        <div className="flex flex-1 flex-wrap items-center justify-between gap-s">
          <EmptyValueDash
            row={row}
            reason="Currently unable to estimate this customer's limit."
          />
          {subtext && <ExpirySubtextLabel subtext={subtext} />}
        </div>
        {!amountOnly && <CopyEmailButton email={row.email} />}
      </div>
    );
  }

  if (amountOnly) {
    return (
      <div className={amountClassName}>{displayCurrency(row.limit_amount)}</div>
    );
  }

  return (
    <div className="flex w-full items-center gap-l">
      <div className="flex flex-1 flex-wrap justify-between gap-s">
        <div className={amountClassName}>
          {displayCurrency(row.limit_amount)}
        </div>
        {subtext && <ExpirySubtextLabel subtext={subtext} />}
      </div>
      <CopyEmailButton email={row.email} />
    </div>
  );
};
